import React, { useEffect, useRef } from "react";
import VimeoPlayer from "@vimeo/player";
import { VIMEO_VIDEOS } from "./lessonConstants";

/**
 * Vimeo player wrapper. Wires the iframe element + handlers; exposes the
 * player instance to the parent via `playerRef` so other hooks (e.g. page-time
 * tracking) can call getDuration().
 *
 * CRITICAL: deps for the player init effect are `[user?.id, n]` — NOT `[user, n]`.
 * Any mutation of the `user` object (e.g. consultation_booked flipping to true
 * after the dialog submits) would otherwise destroy + recreate the player,
 * restarting the video from 0.
 */
export default function LessonVideoPlayer({ n, title, user, accessGranted, playerRef, onPlay, onTimeUpdate, onEnded }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!iframeRef.current || !user || !accessGranted) return;
    const player = new VimeoPlayer(iframeRef.current);
    playerRef.current = player;
    player.on("play", onPlay);
    player.on("timeupdate", onTimeUpdate);
    player.on("ended", onEnded);
    return () => {
      // NEVER call player.destroy() here: @vimeo/player removes the iframe
      // from the DOM, but React still thinks it's mounted → black video on
      // re-render (StrictMode double-invoke in dev, lesson→lesson nav in prod).
      try {
        player.off("play"); player.off("timeupdate"); player.off("ended");
      } catch { /* noop */ }
      playerRef.current = null;
    };
  }, [user?.id, n]);

  const v = VIMEO_VIDEOS[n];
  return (
    <div className="bg-black rounded-2xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]" style={{ aspectRatio: "16 / 9" }}>
      <iframe
        ref={iframeRef}
        data-testid={`lesson-video-${n}`}
        src={`https://player.vimeo.com/video/${v.id}?h=${v.h}&badge=0&autopause=0&title=0&byline=0&portrait=0&dnt=1`}
        title={title}
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        className="w-full h-full block"
        style={{ border: 0 }}
      />
    </div>
  );
}
